import React, { useState, useEffect, useContext } from "react";
import { Icon, PrimaryButton, DefaultButton, TextField, ChoiceGroup } from "@fluentui/react";
import { t } from "i18next";
import "./ExternalFirmConfig.css";
import themeContext from "../../../context/theme/ThemeContext";
import loaderContext from "../../../context/loader/LoaderContext";
import toastContext from "../../../context/toast/ToastContext";
import userContext from "../../../context/users/UserContext";
import CustomBreadCrumb from "../../../components/CustomBreadCrumb";
import CustomToolTipHost from "../../../components/CustomToolTipHost/CustomToolTipHost";
import MultiSelectClientDropdown from "../../MyTemplate/Components/MultiSelectClientDropdown";
import {
  getExternalFirmDetails,
  saveExternalFirm,
} from "../../../service/MasterService";
import { TOAST_TYPE, USERTYPES } from "../../../constants/Constants";
import SearchableDropDown from "../../ReportTemplates/helper/SearchDropdown";
import OutsideClickHandler from "react-outside-click-handler";
import { echoPacsNode } from "../../../service/FirmServices";
import { Accordion, AccordionItem, AccordionHeader, AccordionPanel } from "@fluentui/react-accordion";
import ApiKeysSection from "../../../components/APIkeys/ApikeysSection";

const ExternalFirmConfig = () => {
  const { getThemeColor } = useContext(themeContext);
  const { setLoaderRunning, ref } = useContext(loaderContext);
  const { showToastv2 } = useContext(toastContext);
  const { loadByUsername, authenticated, roles } = useContext(userContext);

  const accentBlue = getThemeColor("active-side-tab");

  const descriptionTypeOptions = [
    { key: 0, text: "Partial" },
    { key: 1, text: "Exact" },
  ];

  const choiceGroupStyles = {
    label: {
      display: "inline",
    },
    flexContainer: {
      rowGap: "4px",
      display: "inline-flex",
      flexDirection: "column",
      flexWrap: "nowrap",
    },
  };

  const requiredStyle = { color: "red" };

  const MAX = {
    aeTitle: 16,
    hostname: 255,
    port: 5,
    delay: 5,
    studyDescription: 255,
  };

  const leftPanelCellTooltipStyles = {
    root: {
      display: "block",
      width: "100%",
      minWidth: 0,
      overflow: "hidden",
    },
  };

  const leftPanelListToolTipProps = {
    calloutProps: {
      styles: {
        beak: { background: getThemeColor("tooltip") },
        beakCurtain: { background: getThemeColor("tooltip") },
        calloutMain: {
          background: getThemeColor("tooltip"),
          maxWidth: 480,
          whiteSpace: "normal",
          wordBreak: "break-word",
          padding: "8px 12px",
        },
        borderRadius: "5px",
        boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
      },
    },
  };

  const ruleTypeOptions = [
    { key: 0, text: "Include"},
    { key: 1, text: "Exclude" },
  ];

  const [firms, setFirms] = useState([]);
  const [modalitiesSubModalities, setModalitiesSubModalities] = useState([]);
  const [selectedFirmId, setSelectedFirmId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [modalityOptions, setModalityOptions] = useState([]);
  const [filteredModalityOptions, setFilteredModalityOptions] = useState([]);
  const [filteredRadiologistOptions, setFilteredRadiologistOptions] = useState([]);
  const [backupFirm, setBackupFirm] = useState(null);
  const [clientDetails, setClientDetails] = useState({});
  const [scopes,setScopes]=useState([]);

  const selectedFirm = firms.find((f) => f.id === selectedFirmId);
  const displayFirm = isEditing ? editingData : selectedFirm;

  useEffect(() => {
    if (!authenticated) loadByUsername();
    init();
  }, [authenticated]);

  const mapClientsListToOptions = (clientsList = []) =>
    clientsList.map((c) => ({
      value: c.id,
      label: c.name,
    }));

  const applyReferenceData = (data = {}) => {
    setModalitiesSubModalities(data.modalityWithSubModality || []);
    const options = (data.modalityWithSubModality || []).map((m) => ({
      key: m.id,
      text: m.name,
    }));
    setModalityOptions(options);
    setFilteredModalityOptions(options);
  };

  const init = async () => {
    setLoaderRunning(true);
    ref.current.continuousStart();
    try {
      const response = await getExternalFirmDetails();
      if (response?.status === 200 && response.data?.result === "SUCCESS") {
        const data = response.data.data || {};
        applyReferenceData(data);
        setFirms(data.externalFirmList || []);
        setScopes(data?.scopes);
      } else {
        applyReferenceData({});
        setFirms([]);
      }
    } catch (err) {
      console.error(err);
      applyReferenceData({});
      setFirms([]);
      showToastv2(TOAST_TYPE.ERROR, t("external-firm-config.initial-load-failed"));
    } finally {
      setLoaderRunning(false);
      ref.current.complete();
    }
  };

  const getRuleType = (type) => {
    const m = ruleTypeOptions.find(x => x.key === type);
    return m ? m.text : "-";
  };

  const getClientLabels = (clientIds, clientsList = []) => {
    const options = mapClientsListToOptions(clientsList);
    return (clientIds || [])
      .map(
        (id) =>
          options.find((c) => String(c.value) === String(id))?.label
      )
      .filter(Boolean)
      .join(", ");
  };

  const handleSelectFirm = (firm) => {
    if (!isEditing) {
      setSelectedFirmId(firm.id);
    }
  };

  const handleEditClick = (e, firm) => {
    e.stopPropagation();
    if (isEditing) return;
    setSelectedFirmId(firm.id);
    setBackupFirm(JSON.parse(JSON.stringify(firm)));
    setEditingData(JSON.parse(JSON.stringify(firm)));
    setIsEditing(true);
    setFilteredRadiologistOptions(getRadiologistDropdownOptions(firm));
  };

  const exitEditMode = () => {
    setIsEditing(false);
    setEditingData(null);
    setBackupFirm(null);
  };

  const computeSetupComplete = (firm) =>
    !!(
      firm?.aeTitle?.trim() &&
      firm?.hostname?.trim() &&
      firm?.port?.toString().trim() &&
      firm?.delay?.toString().trim() &&
      firm?.defaultRadiologistId
    );

  const enrichSavedFirm = (firm) => {
    const rad = (firm.radiologistList || []).find(
      (r) => r.id === firm.defaultRadiologistId
    );
    return {
      ...firm,
      defaultRadiologistName:
        rad?.name || rad?.fullName || firm.defaultRadiologistName,
      setupComplete: computeSetupComplete(firm),
    };
  };

  const validateField = (field, value) => {
    if (field === "aeTitle") {
      if (!value?.trim()) return t("external-firm-config.ae-title-required");
      if (value.length > MAX.aeTitle)
        return t("external-firm-config.ae-title-max", { max: MAX.aeTitle });
      return "";
    }
    if (field === "hostname") {
      if (!value?.trim()) return t("external-firm-config.hostname-required");
      if (value.length > MAX.hostname)
        return t("external-firm-config.hostname-max", { max: MAX.hostname });
      return "";
    }
    if (field === "port") {
      if (!value?.toString().trim()) return t("external-firm-config.port-required");
      if (!/^\d+$/.test(value.toString()))
        return t("external-firm-config.port-numeric");
      if (value.toString().length > MAX.port)
        return t("external-firm-config.port-max", { max: MAX.port });
      return "";
    }
    if (field === "delay") {
      if (!value?.toString().trim()) return t("external-firm-config.delay-required");
      if (!/^\d+$/.test(value.toString()))
        return t("external-firm-config.delay-numeric");
      if (value.toString().length > MAX.delay)
        return t("external-firm-config.delay-max", { max: MAX.delay });
      if (Number(value) <= 0) return t("external-firm-config.delay-positive");
      return "";
    }
    if (field === "defaultRadiologistId") {
      if (!value) return t("external-firm-config.select-radiologist");
      return "";
    }
    return "";
  };

  const buildSavePayload = (firm) => ({
    id: firm.id,
    aeTitle: firm.aeTitle?.trim(),
    hostname: firm.hostname?.trim(),
    port: firm.port,
    delay: firm.delay,
    defaultRadiologistId: firm.defaultRadiologistId,
    rules: (firm.rules || []).map((rule) => {
      const transformed = {
        ruleType: rule.ruleType ?? 0,
        clients: rule.clients || [],
        modalityId: rule.modalityId,
        studyDescription: rule.studyDescription || null,
        descriptionMatchType: rule.descriptionMatchType ?? 0,
      };
      if (rule.id != null) transformed.id = rule.id;
      if (rule.subModalityIds?.length) {
        transformed.subModalityIds = rule.subModalityIds;
      }
      return transformed;
    }),
  });

  const handleCancel = () => {
    if (backupFirm) {
      setFirms((prev) =>
        prev.map((f) => (f.id === backupFirm.id ? backupFirm : f))
      );
    }
    exitEditMode();
  };

  const handleApiKeysChange = (nextApiKeys) => {
    if (!displayFirm?.id) return;

    const firmId = displayFirm.id;
    const updateCredentials = (firm) =>
      firm?.id === firmId
        ? { ...firm, clientCredentials: nextApiKeys }
        : firm;

    setFirms((prev) => prev.map(updateCredentials));
    setEditingData((prev) => updateCredentials(prev));
    setBackupFirm((prev) => updateCredentials(prev));
  };

  const handleSave = async () => {
    if (!editingData) return;

    const fieldsToValidate = [
      { field: "aeTitle", value: editingData.aeTitle },
      { field: "hostname", value: editingData.hostname },
      { field: "port", value: editingData.port },
      { field: "delay", value: editingData.delay },
      {
        field: "defaultRadiologistId",
        value: editingData.defaultRadiologistId,
      },
    ];

    for (const item of fieldsToValidate) {
      const errorMsg = validateField(item.field, item.value);
      if (errorMsg) {
        showToastv2(TOAST_TYPE.INFO, errorMsg);
        return;
      }
    }

    for (let i = 0; i < (editingData.rules || []).length; i++) {
      const rule = editingData.rules[i];
      if (!rule.clients?.length) {
        showToastv2(
          TOAST_TYPE.INFO,
          t("external-firm-config.rule-client-required", { index: i + 1 })
        );
        return;
      }
      if (!rule.modalityId) {
        showToastv2(
          TOAST_TYPE.INFO,
          t("external-firm-config.rule-modality-required", { index: i + 1 })
        );
        return;
      }
      if (
        rule.studyDescription &&
        rule.studyDescription.length > MAX.studyDescription
      ) {
        showToastv2(
          TOAST_TYPE.INFO,
          t("external-firm-config.rule-study-desc-max", { index: i + 1 })
        );
        return;
      }
    }

    const payload = buildSavePayload(editingData);
    setLoaderRunning(true);
    ref.current.continuousStart();

    try {
      const response = await saveExternalFirm(payload);
      if (response?.status === 200 && response.data?.result === "SUCCESS") {
        const savedFirm = enrichSavedFirm({
          ...editingData,
          rules: editingData.rules || [],
        });
        setFirms((prev) =>
          prev.map((f) => (f.id === savedFirm.id ? savedFirm : f))
        );
        setSelectedFirmId(savedFirm.id);
        showToastv2(TOAST_TYPE.SUCCESS, t("external-firm-config.save-success"));
        exitEditMode();
      } else {
        showToastv2(TOAST_TYPE.ERROR, t("external-firm-config.save-failed"));
      }
    } catch (err) {
      console.error(err);
      showToastv2(TOAST_TYPE.ERROR, t("external-firm-config.save-failed"));
    } finally {
      setLoaderRunning(false);
      ref.current.complete();
    }
  };

  const updateEditingField = (field, value) => {
    setEditingData((prev) => ({ ...prev, [field]: value }));
  };

  const getFirmClientOptions = (firm) =>
    mapClientsListToOptions(firm?.clientsList || []);

  const getSubmodalityOptions = (modalityId) => {
    const modality = modalitiesSubModalities.find((m) => m.id === modalityId);
    if (!modality || !modality.subModality) return [];
    return modality.subModality.map((sm) => ({
      value: sm.id,
      label: `${sm.serviceCode}: ${sm.serviceName}`,
    }));
  };

  const handleAddRule = () => {
    setEditingData((prev) => ({
      ...prev,
      rules: [
        ...(prev.rules || []),
        {
          ruleType: 0,
          clients: [],
          modalityId: null,
          subModalityIds: [],
          studyDescription: "",
          descriptionMatchType: 0,
        },
      ],
    }));
  };

  const handleModalityChange = (index, modalityId) => {
    const updatedRules = [...(editingData.rules || [])];
    updatedRules[index] = {
      ...updatedRules[index],
      modalityId,
      subModalityIds: [],
    };
    updateEditingField("rules", updatedRules);
  };

  const handleUpdateRule = (index, field, value) => {
    const updatedRules = [...(editingData.rules || [])];
    updatedRules[index][field] = value;
    updateEditingField("rules", updatedRules);
  };

  const handleDeleteRule = (index) => {
    updateEditingField(
      "rules",
      (editingData.rules || []).filter((_, i) => i !== index)
    );
  };

  const handleEchoTest = async () => {
    const firm = displayFirm;
    if (!firm?.aeTitle || !firm?.port || !firm?.hostname || !firm?.delay) {
      showToastv2(TOAST_TYPE.SUSPECT, t("external-firm-config.echo-fields-required"));
      return;
    }

    setLoaderRunning(true);
    ref.current.continuousStart();
    try {
      const response = await echoPacsNode({
        aeTitle: firm.aeTitle,
        hostname: firm.hostname,
        port: firm.port,
        name: firm.name,
      });
      if (response?.status === 200) {
        if (response.data.data.status === "SUCCESS") {
          showToastv2(TOAST_TYPE.SUCCESS, t("external-firm-config.echo-success"));
        } else {
          showToastv2(TOAST_TYPE.SUSPECT, t("external-firm-config.echo-failed"));
        }
      } else {
        showToastv2(TOAST_TYPE.ERROR, t("external-firm-config.echo-error"));
      }
    } catch (err) {
      console.error(err);
      showToastv2(TOAST_TYPE.ERROR, t("external-firm-config.echo-error"));
    } finally {
      setLoaderRunning(false);
      ref.current.complete();
    }
  };

  const getRadiologistDropdownOptions = (firm) =>
    (firm?.radiologistList || []).map((r) => ({
      key: r.id,
      text: r.name || r.fullName || r.username,
    }));

  const onDropDownSearch = (searchValue, initialValues) => {
    if (searchValue === null || searchValue === "") {
      return [...initialValues];
    }
    return [...initialValues].filter((i) =>
      i.text.toLowerCase().includes(searchValue.toLowerCase())
    );
  };

  const dropDownStyle = {
    dropdown: { width: "100%" },
    dropdownItemSelected: { fontSize: "12px", lineHeight: "18px", fontWeight: "600" },
    dropdownItemsWrapper: { maxHeight: "300px", overflowY: "auto" },
    dropdownItem: { fontSize: "12px", lineHeight: "18px", fontWeight: "600", color: "white" },
  };

  const compactDropDownStyle = {
    dropdown: { width: "100%", minWidth: 0, maxWidth: "100%" },
    title: { height: 32, lineHeight: 30, fontSize: 14 },
    dropdownItemSelected: { fontSize: 14, lineHeight: "20px", fontWeight: "600" },
    dropdownItemsWrapper: { maxHeight: "200px", overflowY: "auto" },
    dropdownItem: { fontSize: 14, lineHeight: "20px", fontWeight: "600" },
  };

  const nodeTextFieldStyles = {
    root: { width: "100%", maxWidth: "100%", minWidth: 0 },
    fieldGroup: { width: "100%", maxWidth: "100%", minWidth: 0, height: 32 },
    field: { minWidth: 0, fontSize: 14 },
  };

  const handleClickOutside = () => {
    setFilteredModalityOptions(modalityOptions);
    if (editingData) {
      setFilteredRadiologistOptions(getRadiologistDropdownOptions(editingData));
    }
  };

  const getModalityName = (id) => {
    const m = modalitiesSubModalities.find((x) => x.id === id);
    return m ? m.name : "-";
  };

  const getSubmodalityLabel = (subId) => {
    for (const m of modalitiesSubModalities) {
      const sm = m.subModality?.find((s) => s.id === subId);
      if (sm) return `${sm.serviceCode}: ${sm.serviceName}`;
    }
    return "-";
  };

  const getSubmodalityLabels = (subIds = []) =>
    subIds
      .map((id) => getSubmodalityLabel(id))
      .filter((label) => label && label !== "-")
      .join(", ") || "-";

  const getDescriptionMatchType = (type) => {
    const m = descriptionTypeOptions.find((x) => x.key === type);
    return m ? m.text : "-";
  };

  const getRadiologistLabel = (radId, firm) => {
    const rad = (firm?.radiologistList || []).find((r) => r.id === radId);
    return rad ? rad.name || rad.fullName || "-" : "-";
  };

  const renderSetupIcon = (setupComplete) => (
    <CustomToolTipHost
      content={
        setupComplete
          ? t("external-firm-config.setup-complete")
          : t("external-firm-config.setup-incomplete")
      }
    >
      <Icon
        iconName={setupComplete ? "Completed" : "Warning"}
        className={setupComplete ? "setup-icon-complete" : "setup-icon-incomplete"}
      />
    </CustomToolTipHost>
  );

  return (
    <div className="external-firm-container">
      <div className="breadCrumb">
        <CustomBreadCrumb items={[{ text: t("external-firm-config.title") }]} />
      </div>

      <div className="split-container">
        <div className="left-panel">
          <table className={`firms-table${isEditing ? " edit-locked" : ""}`}>
            <thead>
              <tr>
                <th style={{ width: "35%", textAlign: "left" }}>
                  {t("external-firm-config.external-firms")}
                </th>
                <th style={{ width: "45%", textAlign: "left" }}>
                  {t("external-firm-config.clients")}
                </th>
                <th style={{ width: "20%", textAlign: "left" }}>
                  {t("external-firm-config.action")}
                </th>
              </tr>
            </thead>
            <tbody>
              {firms.map((firm) => {
                const isSelected = firm.id === selectedFirmId;
                const clientLabelsText = (firm.clientsList || [])
                  .map((c) => c.name)
                  .filter(Boolean)
                  .join(", ");
                const firmNameText = firm.name || "-";
                return (
                  <tr
                    key={firm.id}
                    className={isSelected ? "firm-row-selected" : ""}
                    onClick={() => handleSelectFirm(firm)}
                  >
                    <td>
                      <CustomToolTipHost
                        content={firmNameText}
                        styles={leftPanelCellTooltipStyles}
                        toolTipProps={leftPanelListToolTipProps}
                      >
                        <div className="single-line-ellipsis">{firmNameText}</div>
                      </CustomToolTipHost>
                    </td>
                    <td>
                      <CustomToolTipHost
                        content={
                          clientLabelsText || t("external-firm-config.no-clients")
                        }
                        styles={leftPanelCellTooltipStyles}
                        toolTipProps={leftPanelListToolTipProps}
                      >
                        <div className="single-line-ellipsis">
                          {clientLabelsText || (
                            <span className="placeholder">
                              {t("external-firm-config.no-clients")}
                            </span>
                          )}
                        </div>
                      </CustomToolTipHost>
                    </td>
                    <td>
                      <div className="actions">
                        {renderSetupIcon(firm.setupComplete)}
                        {roles[0] === USERTYPES.FIRMADMIN && !isEditing && (
                          <CustomToolTipHost content={t("external-firm-config.edit")}>
                            <Icon
                              className="edit-icon"
                              iconName="Edit"
                              styles={{
                                root: { cursor: "pointer", marginLeft: 8 },
                              }}
                              onClick={(e) => handleEditClick(e, firm)}
                            />
                          </CustomToolTipHost>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="right-panel" style={{ background: accentBlue }}>
          {displayFirm ? (
            <>
              <div className="top-details-container modal-scroll">
                {roles[0] === USERTYPES.FIRMADMIN && (
                  <div className="config-section node-config-section">
                    <h3>{t("external-firm-config.node-configuration")}</h3>
                    <div className="node-config-layout">
                      <div className="node-config-left">
                        <div className="node-config-row">
                          <div className="detail-item node-field node-field-ae">
                      <label>
                        {t("external-firm-config.ae-title")}
                        {isEditing && <span style={requiredStyle}> *</span>}
                      </label>
                      {isEditing ? (
                        <TextField
                          styles={nodeTextFieldStyles}
                          value={editingData.aeTitle || ""}
                          onChange={(e) => updateEditingField("aeTitle", e.target.value)}
                        />
                      ) : (
                        <div className="detail-value">{displayFirm.aeTitle || "-"}</div>
                      )}
                    </div>
                    <div className="detail-item node-field node-field-host">
                      <label>
                        {t("external-firm-config.hostname")}
                        {isEditing && <span style={requiredStyle}> *</span>}
                      </label>
                      {isEditing ? (
                        <TextField
                          styles={nodeTextFieldStyles}
                          value={editingData.hostname || ""}
                          onChange={(e) => updateEditingField("hostname", e.target.value)}
                        />
                      ) : (
                        <div className="detail-value">{displayFirm.hostname || "-"}</div>
                      )}
                    </div>
                    <div className="detail-item node-field node-field-port">
                      <label>
                        {t("external-firm-config.port")}
                        {isEditing && <span style={requiredStyle}> *</span>}
                      </label>
                      {isEditing ? (
                        <TextField
                          styles={nodeTextFieldStyles}
                          type="number"
                          value={editingData.port || ""}
                          onChange={(e) => updateEditingField("port", e.target.value)}
                        />
                      ) : (
                        <div className="detail-value">{displayFirm.port || "-"}</div>
                      )}
                    </div>
                    <div className="detail-item node-field node-field-delay">
                      <label>
                        {t("external-firm-config.delay")}
                        {isEditing && <span style={requiredStyle}> *</span>}
                      </label>
                      {isEditing ? (
                        <TextField
                          styles={nodeTextFieldStyles}
                          type="number"
                          value={editingData.delay || ""}
                          onChange={(e) => updateEditingField("delay", e.target.value)}
                        />
                      ) : (
                        <div className="detail-value">{displayFirm.delay || "-"}</div>
                      )}
                    </div>
                    <div className="detail-item echo-item">
                      {isEditing && (
                        <label>{t("external-firm-config.action")}</label>
                      )}
                      <PrimaryButton onClick={handleEchoTest} style={{ width: "50px", minWidth: "50px" }}>
                        {t("external-firm-config.echo")}
                      </PrimaryButton>
                    </div>
                      </div>
                    </div>
                    <div className="node-config-radiologist-section">
                      <div className="detail-item radiologist-item">
                        <label>
                          {t("external-firm-config.default-radiologist-configuration")}
                          {isEditing && <span style={requiredStyle}> *</span>}
                        </label>
                        {isEditing ? (
                          <OutsideClickHandler
                            onOutsideClick={() =>
                              setFilteredRadiologistOptions(
                                getRadiologistDropdownOptions(editingData)
                              )
                            }
                          >
                            <div className="radiologist-dropdown-wrap">
                              <SearchableDropDown
                                className="external-firm-radiologist-dropdown"
                                dropDownStyle={compactDropDownStyle}
                                placeholder={t("external-firm-config.select-radiologist-placeholder")}
                                options={filteredRadiologistOptions}
                                selectedKey={editingData.defaultRadiologistId}
                                onChange={(e, option) =>
                                  updateEditingField("defaultRadiologistId", option?.key ?? null)
                                }
                                onSearchValueChanged={(searchValue) => {
                                  setFilteredRadiologistOptions(
                                    onDropDownSearch(
                                      searchValue,
                                      getRadiologistDropdownOptions(editingData)
                                    )
                                  );
                                }}
                              />
                            </div>
                          </OutsideClickHandler>
                        ) : (
                          <div className="detail-value radiologist-compact-value">
                            {displayFirm.defaultRadiologistName ||
                              getRadiologistLabel(
                                displayFirm.defaultRadiologistId,
                                displayFirm
                              ) ||
                              "-"}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {roles[0] === USERTYPES.FIRMADMIN && (
                                    <ApiKeysSection
                                      isEditing={isEditing}
                                      externalFirmId={displayFirm.id}
                                      firms={getFirmClientOptions(editingData)}
                                      apiKeys={displayFirm.clientCredentials}
                                      scopes={scopes}
                                      onApiKeysChange={handleApiKeysChange}
                                    />
                                  )}
                <div className="config-section rules-section">
                  <div className="rules-header">
                    <h3>{t("external-firm-config.auto-assignment-rules")}</h3>
                    {isEditing && (
                      <PrimaryButton onClick={handleAddRule}>
                        + {t("external-firm-config.add-rule")}
                      </PrimaryButton>
                    )}
                  </div>

                  {!displayFirm.rules?.length && !isEditing ? (
                    <div className="no-rules">{t("external-firm-config.no-rules")}</div>
                  ) : !displayFirm.rules?.length && isEditing ? (
                    <div className="no-rules">{t("external-firm-config.no-rules")}</div>
                  ) : (
                    <div className="rules-table-wrapper">
                      <table className="rules-table2">
                        <thead>
                          <tr>
                            <th className="col-match-type" style={{ textAlign: "left" }}>Rule Type
                              {isEditing && <span style={requiredStyle}> *</span>}
                            </th>
                            <th className="col-client" style={{ textAlign: "left" }}>
                              {t("external-firm-config.client")}
                              {isEditing && <span style={requiredStyle}> *</span>}
                            </th>
                            <th className="col-modality" style={{ textAlign: "left" }}>
                              {t("external-firm-config.modality")}
                              {isEditing && <span style={requiredStyle}> *</span>}
                            </th>
                            <th className="col-submodality" style={{ textAlign: "left" }}>
                              {t("external-firm-config.sub-modality")}
                            </th>
                            <th className="col-study-desc" style={{ textAlign: "left" }}>
                              {t("external-firm-config.study-description")}
                            </th>
                            <th className="col-match-type" style={{ textAlign: "left" }}>
                              {t("external-firm-config.match-type")}
                            </th>
                            {isEditing && (
                              <th className="col-action" style={{ textAlign: "left" }}>
                                {t("external-firm-config.action")}
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {(displayFirm.rules || []).map((rule, idx) => {
                            const subModalityLabels = getSubmodalityLabels(
                              rule.subModalityIds ?? []
                            );
                            return (
                            <tr key={rule.id || idx}>
                              {isEditing ? (
                                <>
                                  <td className="col-match-type">
                                    <div className="match-type-group">
                                      <ChoiceGroup
                                          selectedKey={
                                            rule.ruleType || 0
                                          }
                                          options={ruleTypeOptions}
                                          styles={choiceGroupStyles}
                                          //disabled={!rule.studyDescription}
                                          onChange={(e, option) =>
                                            handleUpdateRule(
                                              idx,
                                              "ruleType",
                                              option.key
                                            )
                                          }
                                      />
                                    </div>
                                  </td>
                                  <td className="col-client">
                                    <MultiSelectClientDropdown
                                      options={getFirmClientOptions(editingData)}
                                      value={getFirmClientOptions(editingData).filter((o) =>
                                        (rule.clients ?? []).includes(o.value)
                                      )}
                                      onChange={(selectedOptions) =>
                                        handleUpdateRule(
                                          idx,
                                          "clients",
                                          selectedOptions
                                            ? selectedOptions.map((opt) => opt.value)
                                            : []
                                        )
                                      }
                                      placeholder={t("external-firm-config.select-clients")}
                                      getThemeColor={getThemeColor}
                                      addSelectAll
                                      useMenuPortal
                                    />
                                  </td>
                                  <td className="col-modality">
                                    <OutsideClickHandler onOutsideClick={handleClickOutside}>
                                      <SearchableDropDown
                                        dropDownStyle={dropDownStyle}
                                        selectedKey={rule.modalityId}
                                        onChange={(e, option) =>
                                          handleModalityChange(idx, option?.key ?? null)
                                        }
                                        onSearchValueChanged={(searchValue) => {
                                          setFilteredModalityOptions(
                                            onDropDownSearch(searchValue, modalityOptions)
                                          );
                                        }}
                                        options={filteredModalityOptions}
                                        className="external-firm-modality-dropdown modal-scroll"
                                        placeholder={t("external-firm-config.select-modality")}
                                      />
                                    </OutsideClickHandler>
                                  </td>
                                  <td className="col-submodality">
                                    <MultiSelectClientDropdown
                                      options={getSubmodalityOptions(rule.modalityId)}
                                      value={getSubmodalityOptions(rule.modalityId).filter((o) =>
                                        (rule.subModalityIds ?? []).includes(o.value)
                                      )}
                                      onChange={(selectedOptions) =>
                                        handleUpdateRule(
                                          idx,
                                          "subModalityIds",
                                          selectedOptions
                                            ? selectedOptions.map((opt) => opt.value)
                                            : []
                                        )
                                      }
                                      placeholder={t("external-firm-config.select-sub-modality")}
                                      noOptionsMessage={t("external-firm-config.no-options-available")}
                                      getThemeColor={getThemeColor}
                                      addSelectAll={!!rule.modalityId && !rule.studyDescription?.trim()}
                                      useMenuPortal
                                      isDisabled={
                                        !rule.modalityId ||
                                        !!rule.studyDescription?.trim()
                                      }
                                    />
                                  </td>
                                  <td className="col-study-desc">
                                    <TextField
                                      value={rule.studyDescription || ""}
                                      onChange={(e) =>
                                        handleUpdateRule(idx, "studyDescription", e.target.value)
                                      }
                                      placeholder={t("external-firm-config.study-description-placeholder")}
                                      disabled={(rule.subModalityIds ?? []).length > 0}
                                    />
                                  </td>
                                  <td className="col-match-type">
                                    <div className="match-type-group">
                                      <ChoiceGroup
                                        selectedKey={
                                          rule.descriptionMatchType
                                        }
                                        options={descriptionTypeOptions}
                                        styles={choiceGroupStyles}
                                        disabled={!rule.studyDescription}
                                        onChange={(e, option) =>
                                          handleUpdateRule(
                                            idx,
                                            "descriptionMatchType",
                                            option.key
                                          )
                                        }
                                      />
                                    </div>
                                  </td>
                                  <td className="col-action" style={{ textAlign: "center" }}>
                                    <CustomToolTipHost content={t("external-firm-config.delete")}>
                                      <Icon
                                        className="delete-icon"
                                        iconName="Delete"
                                        styles={{ root: { cursor: "pointer" } }}
                                        onClick={() => handleDeleteRule(idx)}
                                      />
                                    </CustomToolTipHost>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td>{getRuleType(rule.ruleType || 0)}</td>
                                  <td className="col-client">
                                    {getClientLabels(
                                      rule.clients,
                                      displayFirm?.clientsList
                                    )}
                                  </td>
                                  <td className="col-modality">{getModalityName(rule.modalityId)}</td>
                                  <td className="col-submodality">
                                    <CustomToolTipHost content={subModalityLabels}>
                                      <div className="single-line-ellipsis">
                                        {subModalityLabels}
                                      </div>
                                    </CustomToolTipHost>
                                  </td>
                                  <td className="col-study-desc">{rule.studyDescription || "-"}</td>
                                  <td className="col-match-type">
                                    {rule.studyDescription
                                      ? getDescriptionMatchType(rule.descriptionMatchType)
                                      : "-"}
                                  </td>
                                </>
                              )}
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                 
              </div>

              {isEditing && (
                <div className="action-buttons-strip">
                  <div className="button-group-left">
                    <DefaultButton onClick={handleCancel}>
                      {t("external-firm-config.cancel")}
                    </DefaultButton>
                    <PrimaryButton onClick={handleSave}>
                      {t("external-firm-config.save")}
                    </PrimaryButton>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="no-selection">
              <p>
                {firms.length === 0
                  ? t("external-firm-config.no-firms")
                  : t("external-firm-config.select-firm-hint")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExternalFirmConfig;
